import styles from './LocalePicker.scss';

import i18n from 'i18next';
import React from 'react';
import { Image } from 'src/components/Image/Image';
import { CURRENT_I18N_KEY } from 'src/i18n.config';
import { getBaseUrlFromLocale } from 'src/utils/AppRoute';
import { Environment } from 'src/utils/Environment';
import { CountryCode, DataCenter, LanguageCode, Locale, generateUrl, getDataCenter, getLocalesForDataCenter } from 'src/utils/Locales';
import { enableInternationalRedirect } from 'src/utils/Region';

import { LocalStorage } from 'src/utils/LocalStorage';

type SameDataCenterLocaleProps = {
  locale: Locale;
  changeLanguage: (locale: Locale) => void;
  isInternationalRedirectOn: boolean;
};

// builds the new path with locale information
export const buildNewPath = (baseUrl: string, slug: string) => {
  if (!baseUrl && !slug) return '';
  if (baseUrl && !slug) return baseUrl;

  return baseUrl && baseUrl !== '/' ? `${baseUrl}/${slug}` : `/${slug}`;
};

// filters locale information & empty strings from the url path and returns the slug
export const removeLocalesFromPath = (path: string) => {
  const countryCodeList = Object.values(CountryCode).map(each => each.toLowerCase());
  const languageCodeList = Object.values(LanguageCode);
  // creates an array of the current path, omitting empty strings
  const urlPathAsArray = path.split('/').filter(Boolean);
  return urlPathAsArray
    .filter((each, index, array) => {
      // locale information is prepended so only run the check if it's the first or second index
      if (index === 0) return !countryCodeList.includes(each as CountryCode);
      if (index === 1) return !languageCodeList.includes(each as LanguageCode) || !countryCodeList.includes(array[0] as CountryCode);
      return true;
    })
    .join()
    .replace(/,/g, '/');
};

export const SameDataCenterLocale: React.FunctionComponent<SameDataCenterLocaleProps> = ({ locale: l, changeLanguage, isInternationalRedirectOn, children }) => {
  const { pathname: currrentPath } = window.location;
  const slug = removeLocalesFromPath(currrentPath);

  let baseUrl = getBaseUrlFromLocale(l.code) as string;
  if (baseUrl === '') baseUrl = '/';

  const newPath = buildNewPath(baseUrl, slug);

  // sets the language
  const onLocaleChange = (newPath: string, l: Locale) => {
    changeLanguage(l);
  };

  if (isInternationalRedirectOn) {
    return (
      <a href={`${newPath}?from=localePicker`} onClick={() => onLocaleChange(newPath, l)} title={l.displayName} data-test={`${l.code.toLowerCase()}-locale-selector`}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={() => onLocaleChange(newPath, l)} title={l.displayName} data-test={`${l.code.toLowerCase()}-locale-selector`}>
      {children}
    </button>
  );
};

type DifferentDataCenterLocaleProps = {
  locale: Locale;
};

export const DifferentDataCenterLocale: React.FunctionComponent<DifferentDataCenterLocaleProps> = ({ locale, children }) => {
  // Add the locale code to the query params so the correct locale will be selected after going to the href
  return (
    <a href={generateUrl(locale)} title={locale.displayName} data-test={`${locale.code.toLowerCase()}-locale-selector`}>
      {children}
    </a>
  );
};

export interface RegionProps {
  dataCenter: DataCenter;
  closeModal: () => void;
}
export class Region extends React.PureComponent<RegionProps> {
  render() {
    const { dataCenter: dc } = this.props;
    const localesInDC = getLocalesForDataCenter(dc);

    if (!localesInDC.length) return null; // Don't render this data center block if there are no locales

    // Use an anchor tag to redirect the user if the locales are in a different data center
    // treat localhost as always being the same data center
    const isCurrentDataCenter = Environment.current.isLocalhost || getDataCenter().code === dc.code;
    // turns on the feature if a visitor accesses the locale picker from a NA data center
    const isInternationalRedirectOn: boolean = enableInternationalRedirect();

    return (
      <div className={styles.region} key={dc.code}>
        <h4 className={styles.regionTitle}>{dc.displayName}</h4>
        <ul>
          {localesInDC.map(l => (
            <li key={l.code}>
              {(isCurrentDataCenter && (!l.country.subPathOverride || (l.country.subPathOverride && isInternationalRedirectOn))) ? (
                <SameDataCenterLocale locale={l} changeLanguage={this.changeLanguage} isInternationalRedirectOn={isInternationalRedirectOn}>
                  <Image aria-hidden src={l.country.icon} />
                  <span lang={l.code}>{l.displayName}</span>
                </SameDataCenterLocale>
              ) : (
                  <DifferentDataCenterLocale locale={l}>
                    <Image aria-hidden src={l.country.icon} />
                    <span lang={l.code}>{l.displayName}</span>
                  </DifferentDataCenterLocale>
                )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  private changeLanguage = (locale: Locale) => {
    i18n.changeLanguage(locale.code);
    LocalStorage.trySetItem(CURRENT_I18N_KEY, locale.code);
    this.props.closeModal();
  };
}
